/**
 * Recursive shadowcasting field-of-view on the tile grid. Adapted from Björn
 * Bergström's algorithm on Roguebasin. Symmetric, allocation-free (uses a
 * `visit` callback), and range-limited by squared distance.
 *
 * Two entry points:
 *   - `computeFov`         — full 8-octant FOV, no facing filter.
 *   - `computeVisionCone`  — same, but only visits tiles inside a facing cone.
 *
 * Both call `visit(x, y)` for the origin exactly once, and once per visible
 * tile within range. `blocks(x, y)` returns true when the tile is opaque.
 *
 * Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

/** Octant transformation multipliers. Each row is (xx, xy, yx, yy). */
const OCTANTS: ReadonlyArray<readonly [number, number, number, number]> = [
  [1, 0, 0, 1],
  [0, 1, 1, 0],
  [0, -1, 1, 0],
  [-1, 0, 0, 1],
  [-1, 0, 0, -1],
  [0, -1, -1, 0],
  [0, 1, -1, 0],
  [1, 0, 0, -1],
];

export function computeFov(
  originX: number,
  originY: number,
  range: number,
  blocks: (x: number, y: number) => boolean,
  visit: (x: number, y: number) => void,
): void {
  visit(originX, originY);
  const rangeSq = range * range;
  for (const [xx, xy, yx, yy] of OCTANTS) {
    castOctant(originX, originY, 1, 1.0, 0.0, range, rangeSq, xx, xy, yx, yy, blocks, visit);
  }
}

function castOctant(
  ox: number,
  oy: number,
  row: number,
  startSlope: number,
  endSlope: number,
  range: number,
  rangeSq: number,
  xx: number,
  xy: number,
  yx: number,
  yy: number,
  blocks: (x: number, y: number) => boolean,
  visit: (x: number, y: number) => void,
): void {
  if (startSlope < endSlope) return;
  let nextStartSlope = startSlope;
  for (let dist = row; dist <= range; dist++) {
    let blocked = false;
    const dy = -dist;
    for (let dx = -dist; dx <= 0; dx++) {
      const lSlope = (dx - 0.5) / (dy + 0.5);
      const rSlope = (dx + 0.5) / (dy - 0.5);
      if (rSlope > startSlope) continue;
      if (lSlope < endSlope) break;
      const mx = dx * xx + dy * xy;
      const my = dx * yx + dy * yy;
      const x = ox + mx;
      const y = oy + my;
      if (dx * dx + dy * dy <= rangeSq) visit(x, y);
      if (blocked) {
        if (blocks(x, y)) {
          nextStartSlope = rSlope;
          continue;
        } else {
          blocked = false;
          startSlope = nextStartSlope;
        }
      } else if (blocks(x, y) && dist < range) {
        blocked = true;
        castOctant(ox, oy, dist + 1, startSlope, lSlope, range, rangeSq, xx, xy, yx, yy, blocks, visit);
        nextStartSlope = rSlope;
      }
    }
    if (blocked) return;
  }
}

/**
 * Same shadowcasting as `computeFov`, but only visits tiles whose direction
 * from the origin lies inside a cone centred on `(facingDx, facingDy)` with
 * a half-opening of `halfAngleRad`. The origin tile is always visited.
 */
export function computeVisionCone(
  originX: number,
  originY: number,
  facingDx: number,
  facingDy: number,
  range: number,
  halfAngleRad: number,
  blocks: (x: number, y: number) => boolean,
  visit: (x: number, y: number) => void,
): void {
  const facingLen = Math.hypot(facingDx, facingDy);
  if (facingLen === 0) {
    // No facing direction → treat as omnidirectional.
    computeFov(originX, originY, range, blocks, visit);
    return;
  }
  const cosHalf = Math.cos(halfAngleRad);
  computeFov(originX, originY, range, blocks, (x, y) => {
    if (x === originX && y === originY) {
      visit(x, y);
      return;
    }
    const dx = x - originX;
    const dy = y - originY;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) return;
    const cos = (dx * facingDx + dy * facingDy) / (dist * facingLen);
    if (cos >= cosHalf) visit(x, y);
  });
}
